import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { commitMutation, graphql } from 'react-relay';
import { Button, Dropdown, Header, Icon, Item, Modal } from 'semantic-ui-react';
import {
  Analytics,
  DeleteConfirmation,
  GlobalNotifications,
  Intent,
  MobileModal
} from '../../Modules';
import withAuth from '../../App/Authenticate';
import { FloatingLabelInput as Input, SelectableBlock } from '../../Elements';
import MobileHeader from '../../App/MobileHeader';
import styles from './Pipelines.scss';
import Search from '../../App/SearchContainer';
import styleVariables from '../../variables';
import EventCard from '../List/EventCard';
import ListTitle from './ListTitle';
import Formsy from 'formsy-react';
import { Draggable, DragDropContext, Droppable } from 'react-beautiful-dnd';
import { arrayMove } from '../../../lib/utils/array';

const Pipelines = class Pipelines extends React.Component {
  static propTypes = {
    router: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
    sidebarOpen: PropTypes.bool,
    toggleSidebar: PropTypes.func.isRequired,
    viewer: PropTypes.object.isRequired
  };

  constructor(props) {
    super(props);
    const pipelines = _.cloneDeep(this.props.viewer.pipelines);
    this.state = {
      addingPipeline: false,
      addEventModalOpen: false,
      moveEventModalOpen: false,
      addListModalOpen: false,
      listDeleteModalOpen: false,
      newListTitle: '',
      pipelines: this.sortPlistsAndEvents(pipelines)
    };
    this.onValidAddListSubmit = this.onValidAddListSubmit.bind(this);
    this.onInvalidAddListSubmit = this.onInvalidAddListSubmit.bind(this);
    this.closeListDeleteModal = this.closeListDeleteModal.bind(this);
    this.openListDeleteModal = this.openListDeleteModal.bind(this);
    this.createList = this.createList.bind(this);
    this.createPipeline = this.createPipeline.bind(this);
    this.deleteList = this.deleteList.bind(this);
    this.openAddEventModal = this.openAddEventModal.bind(this);
    this.closeAddEventModal = this.closeAddEventModal.bind(this);
    this.openMoveEventModal = this.openMoveEventModal.bind(this);
    this.closeMoveEventModal = this.closeMoveEventModal.bind(this);
    this.openAddListModal = this.openAddListModal.bind(this);
    this.closeAddListModal = this.closeAddListModal.bind(this);
    this.removeEvent = this.removeEvent.bind(this);
    this.updateList = this.updateList.bind(this);
    this.updateWindowDimensions = this.updateWindowDimensions.bind(this);
    this.onDragEnd = this.onDragEnd.bind(this);
  }

  componentWillMount() {
    this.updateWindowDimensions();
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateWindowDimensions);
  }

  componentWillReceiveProps(nextProps) {
    // Update pipelines as they are changed
    const obj = _.cloneDeep(this.state);
    obj.pipelines = this.sortPlistsAndEvents(_.cloneDeep(nextProps.viewer.pipelines));
    this.setState(obj);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowDimensions);
  }

  onValidAddListSubmit() {
    this.createList(
      {
        pipelineId: this.state.activePipelineId,
        title: this.state.newListTitle
      },
      this.closeAddListModal
    );
  }

  onInvalidAddListSubmit() {
    GlobalNotifications.show({
      message: 'Unable to add list',
      intent: Intent.DANGER
    });
  }

  onDragEnd(result) {
    const { source, destination } = result;

    // Not dropped on a droppable
    if (!source || !destination) {
      return;
    }

    // No movement
    if (source.droppableId === destination.droppableId &&
      source.index === destination.index) {
      return;
    }

    // Reorder Columns
    if (result.type === 'COLUMN') {
      this.moveList(source.index, destination.index);
      return;
    }

    // Moving Events
    if (result.type === 'EVENT') {
      // Move events within a list
      if (source.droppableId === destination.droppableId) {
        this.moveEventWithinList({
          source: { listId: source.droppableId, index: source.index },
          destination: { index: destination.index }
        });
        return;
      }

      // Move events between lists
      this.moveEventBetweenLists({
        source: { listId: source.droppableId, index: source.index },
        destination: { listId: destination.droppableId, index: destination.index }
      });
      return;
    }
  }

  getListFromPipeline(id, pipeline) {
    return pipeline.plists.edges.filter((list) => list.node.id === id)[0].node;
  }

  moveList(sourceIndex, destinationIndex) {
    const state = _.cloneDeep(this.state);
    const pipeline = state.pipelines.edges[0].node;
    pipeline.plists.edges = arrayMove(
      pipeline.plists.edges,
      sourceIndex,
      destinationIndex
    );
    pipeline.plists.edges[
      destinationIndex
    ].node.position =
      destinationIndex;
    this.setState(state, () =>
      this.updateList(
        pipeline.plists.edges[destinationIndex]
      )
    );
  }

  moveEventWithinList({ source, destination }) {
    const state = _.cloneDeep(this.state);
    const pipeline = state.pipelines.edges[0].node;
    const sourceList = this.getListFromPipeline(source.listId, pipeline);
    let sourceListEvents = sourceList.plist_events.edges;

    // Move event to correct position
    sourceListEvents = arrayMove(
      sourceListEvents,
      source.index,
      destination.index
    );

    const updatedListEvent = sourceListEvents[
      destination.index
    ].node;

    // Update position of all items in the list
    sourceListEvents = sourceListEvents.map((event, index) => {
      const node = event.node;
      node.position = index;
      return { node };
    });

    // Update local state and call mutation
    this.setState(state, () =>
      this.updateListEvent({
        id: updatedListEvent.id,
        position: updatedListEvent.position,
        sourceList,
        destinationList: sourceList
      })
    );
  }

  moveEventBetweenLists({ source, destination }) {
    const state = _.cloneDeep(this.state);
    const pipeline = state.pipelines.edges[0].node;
    const sourceList = this.getListFromPipeline(source.listId, pipeline);
    const destinationList = this.getListFromPipeline(destination.listId, pipeline);
    let sourceListEvents = sourceList.plist_events.edges;
    let destinationListEvents = destinationList.plist_events.edges;

    // First, add event to destination list
    destinationListEvents.splice(destination.index, 0, sourceListEvents[source.index]);

    // Next, remove event from source list
    sourceListEvents.splice(source.index, 1);

    // Update position of all items in source list
    sourceListEvents = sourceListEvents.map((event, index) => {
      const node = event.node;
      node.position = index;
      return { node };
    });

    // Update position of all items in destination list
    destinationListEvents = destinationListEvents.map((event, index) => {
      const node = event.node;
      node.position = index;
      return { node };
    });

    const updatedListEvent = destinationListEvents[
      destination.index
    ].node;

    // Update local state and call mutation
    this.setState(state, () =>
      this.updateListEvent({
        id: updatedListEvent.id,
        position: updatedListEvent.position,
        sourceList,
        destinationList
      })
    );
  }

  sortPlistsAndEvents(pipelines) {
    // Used to ensure arrays are ordered by element position
    // since Relay updater doesn't seem to guarantee order
    const sortedPipelines = pipelines;
    sortedPipelines.edges = sortedPipelines.edges.map(pipeline => {
      const sortedPipeline = pipeline;
      sortedPipeline.node.plists.edges = _.sortBy(
        sortedPipelines.edges[0].node.plists.edges,
        (o) => o.node.position
      ).map(plist => {
        const withSortedEvents = plist;
        withSortedEvents.node.plist_events.edges = _.sortBy(
          plist.node.plist_events.edges, (o) => o.node.position
        );
        return withSortedEvents;
      });
      return sortedPipeline;
    });
    return sortedPipelines;
  }

  openListDeleteModal({ activePipelineId, activeList }) {
    this.setState({
      listDeleteModalOpen: true,
      activePipelineId,
      activeList
    });
  }

  closeListDeleteModal() {
    this.setState({ listDeleteModalOpen: false });
  }

  openAddEventModal(activeList) {
    this.setState({ addEventModalOpen: true, activeList });
  }

  closeAddEventModal() {
    this.setState({ addEventModalOpen: false });
  }

  openMoveEventModal({ plist, plistEvent }) {
    this.setState({
      moveEventModalOpen: true,
      activeEvent: plistEvent,
      activeList: plist,
      moveEventToList: plist
    });
  }

  closeMoveEventModal() {
    this.setState({ moveEventModalOpen: false });
  }

  openAddListModal(activePipelineId) {
    this.setState({ addListModalOpen: true, activePipelineId });
  }

  closeAddListModal() {
    this.setState({ addListModalOpen: false, newListTitle: '' });
  }

  addEventToList({ eventId, plistId }, successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to add event to pipeline',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (successCb) successCb();
      GlobalNotifications.show({
        message: 'Successfully added event to pipeline',
        intent: Intent.SUCCESS
      });
      Analytics.trackEvent('added-event-to-pipeline');
    };

    const mutation = graphql`
      mutation PipelinesComponent_createPlistEventMutation(
        $input: CreatePlistEventInput!
      ) {
        createPlistEvent(input: $input) {
          new_plist_event_edge {
            node {
              id
              position
              event {
                id
                user_event {
                  booking_status
                  start_at
                  end_at
                  user_event_segments(first: 99999) {
                    edges {
                      node {
                        location
                        room
                      }
                    }
                  }
                  event {
                    id
                    title
                    start_at
                    end_at
                  }
                }
              }
            }
          }
        }
      }
    `;

    const configs = [
      {
        type: 'RANGE_ADD',
        parentID: plistId,
        edgeName: 'new_plist_event_edge',
        connectionInfo: [
          {
            key: 'PipelinesContainer_plist_events',
            rangeBehavior: 'append'
          }
        ]
      }
    ];

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          plist_id: plistId,
          event_id: eventId
        }
      },
      configs,
      onCompleted,
      onError
    });
  }

  createList({ pipelineId, title }, successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to add list',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (successCb) successCb();
      GlobalNotifications.show({
        message: 'Successfully added list',
        intent: Intent.SUCCESS
      });
      Analytics.trackEvent('created-pipeline-list');
    };

    const mutation = graphql`
      mutation PipelinesComponent_createPlistMutation(
        $input: CreatePlistInput!
      ) {
        createPlist(input: $input) {
          new_plist_edge {
            node {
              id
              plist_events(first: 99999)
                @connection(key: "PipelinesContainer_plist_events") {
                edges {
                  node {
                    id
                  }
                }
              }
              position
              title
            }
          }
        }
      }
    `;

    const configs = [
      {
        type: 'RANGE_ADD',
        parentID: pipelineId,
        edgeName: 'new_plist_edge',
        connectionInfo: [
          {
            key: 'PipelinesContainer_plists',
            rangeBehavior: 'append'
          }
        ]
      }
    ];

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          pipeline_id: pipelineId,
          title
        }
      },
      configs,
      onCompleted,
      onError
    });
  }

  createPipeline() {
    this.setState({ addingPipeline: true });
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to add pipeline',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      GlobalNotifications.show({
        message: 'Successfully added pipeline',
        intent: Intent.SUCCESS
      });
      this.setState({ addingPipeline: false });
      Analytics.trackEvent('created-pipeline');
    };

    const mutation = graphql`
      mutation PipelinesComponent_createPipelineMutation(
        $input: CreatePipelineInput!
      ) {
        createPipeline(input: $input) {
          new_pipeline_edge {
            node {
              id
              title
              plists(first: 99999)
                @connection(key: "PipelinesContainer_plists") {
                edges {
                  node {
                    id
                    title
                    position
                    plist_events(first: 99999)
                      @connection(key: "PipelinesContainer_plist_events") {
                      edges {
                        node {
                          id
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const configs = [
      {
        type: 'RANGE_ADD',
        parentID: this.props.viewer.id,
        edgeName: 'new_pipeline_edge',
        connectionInfo: [
          {
            key: 'PipelinesContainer_pipelines',
            rangeBehavior: 'append'
          }
        ]
      }
    ];

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          user_id: this.props.viewer.id,
          title: 'Default Pipeline'
        }
      },
      configs,
      onCompleted,
      onError
    });
  }

  removeEvent({ plist, plistEvent }, successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to remove event',
        intent: Intent.DANGER
      });
    };

    const onCompleted = () => {
      if (successCb) successCb();
      GlobalNotifications.show({
        message: 'Successfully removed event',
        intent: Intent.SUCCESS
      });
      Analytics.trackEvent('deleted-plist-event');
    };

    const mutation = graphql`
      mutation PipelinesComponent_deletePlistEventMutation(
        $input: DeletePlistEventInput!
      ) {
        deletePlistEvent(input: $input) {
          deletedPlistEventId
        }
      }
    `;

    const environment = this.props.relay.environment;
    const parentID = plist.id;

    const configs = [
      {
        type: 'RANGE_DELETE',
        parentID,
        pathToConnection: ['plists', 'plist_events'],
        deletedIDFieldName: 'deletedPlistEventId',
        connectionKeys: [
          {
            key: 'PipelinesContainer_plist_events',
            rangeBehavior: 'append'
          }
        ]
      }
    ];

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          id: plistEvent.id
        }
      },
      configs,
      onCompleted,
      onError
    });
  }

  updateList(list, successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to update pipeline',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (successCb) successCb();
      GlobalNotifications.show({
        message: 'Successfully updated pipeline',
        intent: Intent.SUCCESS
      });
    };

    const mutation = graphql`
      mutation PipelinesComponent_UpdatePlistMutation(
        $input: UpdatePlistInput!
      ) {
        updatePlist(input: $input) {
          pipeline {
            id
            plists(first: 99999) @connection(key: "PipelinesContainer_plists") {
              edges {
                node {
                  id
                  title
                  position
                }
              }
            }
          }
        }
      }
    `;

    const plist = _.pick(list.node, ['id', 'position', 'title']);

    const optimisticResponse = {
      updatePlist: {
        plist
      }
    };
    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          ...plist
        }
      },
      optimisticResponse,
      onCompleted,
      onError
    });
  }

  updateListEvent({ id, position, sourceList, destinationList }, successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to update pipeline',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (successCb) successCb();
    };

    const mutation = graphql`
      mutation PipelinesComponent_UpdatePlistEventMutation(
        $input: UpdatePlistEventInput!
      ) {
        updatePlistEvent(input: $input) {
          current_plist {
            id
            plist_events(first: 99999) @connection(key: "PipelinesContainer_plist_events") {
              edges {
                node {
                  id
                  position
                }
              }
            }
          }
          old_plist {
            id
            plist_events(first: 99999) @connection(key: "PipelinesContainer_plist_events") {
              edges {
                node {
                  id
                  position
                }
              }
            }
          }
        }
      }
    `;

    const optimisticResponse = {
      updatePlistEvent: {
        current_plist: destinationList,
        old_plist: sourceList
      }
    };
    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          id,
          plist_id: destinationList.id,
          position
        }
      },
      optimisticResponse,
      onCompleted,
      onError
    });
  }

  updateWindowDimensions() {
    this.setState({
      window: { width: window.innerWidth, height: window.innerHeight }
    });
  }

  deleteList(successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to delete list',
        intent: Intent.DANGER
      });
    };

    const onCompleted = () => {
      if (successCb) successCb();
      GlobalNotifications.show({
        message: 'Successfully deleted list',
        intent: Intent.SUCCESS
      });
      Analytics.trackEvent('deleted-plist');
    };

    const mutation = graphql`
      mutation PipelinesComponent_deletePlistMutation(
        $input: DeletePlistInput!
      ) {
        deletePlist(input: $input) {
          deletedPlistId
        }
      }
    `;

    const environment = this.props.relay.environment;
    const parentID = this.state.activePipelineId;

    const configs = [
      {
        type: 'RANGE_DELETE',
        parentID,
        pathToConnection: ['pipelines', 'plists'],
        deletedIDFieldName: 'deletedPlistId',
        connectionKeys: [
          {
            key: 'PipelinesContainer_plists',
            rangeBehavior: 'append'
          }
        ]
      }
    ];

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          id: this.state.activeList.id
        }
      },
      configs,
      onCompleted,
      onError
    });
  }

  render() {
    const { relay, router, sidebarOpen, toggleSidebar, viewer } = this.props;
    const AddEventModalContent = (
      <div>
        <h3>Please select the event you would like to add</h3>
        <Search
          viewer={viewer}
          handleResultSelect={(e, data) =>
            this.addEventToList(
              {
                eventId: data.result.eventId,
                plistId: this.state.activeList
              },
              this.closeAddEventModal
            )}
          fluid
        />
      </div>
    );

    const MoveEventModalContent = (
      <div>
        <h3>Please select the list you would like to move this event to</h3>
        <br />
        {viewer.pipelines.edges.length > 0 && viewer.pipelines.edges[0].node.plists.edges.map(plist => (
          <SelectableBlock
            key={plist.node.id}
            checked={(this.state.moveEventToList && this.state.moveEventToList.id) === plist.node.id}
            onChange={() => this.setState({ moveEventToList: plist.node })}
            style={{ marginBottom: '2em' }}
          >
            <h4>{plist.node.title}</h4>
          </SelectableBlock>
        ))}
      </div>
    );

    const AddListModalContent = (
      <div>
        <h3>Give your list a title</h3>
        <Formsy.Form
          onValidSubmit={this.onValidAddListSubmit}
          onInvalidSubmit={this.onInvalidAddListSubmit}
          ref='addListForm'
        >
          <Input
            name='title'
            autoFocus
            value={this.state.newListTitle}
            onChange={e => this.setState({ newListTitle: e.target.value })}
            required
            validationError='Title is required'
          />
        </Formsy.Form>
      </div>
    );

    const eventSettingsMenu = ({ plist, plistEvent }) =>
      <div className={styles.eventSettingsMenu}>
        <Dropdown floating basic icon='ellipsis vertical'>
          <Dropdown.Menu style={{ right: 0, left: 'auto' }}>
            <Dropdown.Item
              text='Move To List...'
              onClick={() => this.openMoveEventModal({ plist, plistEvent })}
            />
            <Dropdown.Divider />
            <Dropdown.Item
              text='Remove From List'
              className={styles.negative}
              onClick={() => this.removeEvent({ plist, plistEvent })}
            />
          </Dropdown.Menu>
        </Dropdown>
      </div>;

    return (
      <div>
        <MobileHeader
          viewer={viewer}
          router={router}
        >
          <div className='menu'>
            <Item
              as='a'
              className={sidebarOpen ? 'active ' : ''}
              onClick={toggleSidebar}
            >
              <Icon name='sidebar' />
            </Item>
          </div>
        </MobileHeader>
        <div>
          <DeleteConfirmation
            delete={() => this.deleteList()}
            key={this.state.activeList}
            open={this.state.listDeleteModalOpen}
            onClose={this.closeListDeleteModal}
            content='All your cards will be removed from this list. Are you sure you want to delete this list?'
          />
          {this.state.window.width >= styleVariables.tabletBreakpoint &&
            <Modal
              open={this.state.moveEventModalOpen}
              size='small'
              onClose={this.closeMoveEventModal}
            >
              <Header content='Move Event To Another List' />
              <Modal.Content>
                {MoveEventModalContent}
              </Modal.Content>
              <Modal.Actions>
                <a
                  className={styles.modalCancel}
                  onClick={this.closeMoveEventModal}
                >
                  Cancel
                </a>
                <Button
                  primary
                  content='Move Event'
                  onClick={() => {
                    this.moveEventBetweenLists({
                      source: {
                        listId: this.state.activeList.id,
                        index: this.state.activeEvent.position
                      },
                      destination: {
                        listId: this.state.moveEventToList.id,
                        index: 0
                      }
                    });
                    this.closeMoveEventModal();
                  }}
                />
              </Modal.Actions>
            </Modal>}
          {this.state.window.width < styleVariables.tabletBreakpoint &&
            <MobileModal open={this.state.moveEventModalOpen}>
              <MobileModal.Header>
                <div className='menu'>
                  <Item as='a' onClick={this.closeMoveEventModal}>
                    Cancel
                  </Item>
                </div>
                <div className='menu right'>
                  <Item
                    as='a'
                    onClick={() => {
                      this.moveEventBetweenLists({
                        source: {
                          listId: this.state.activeList.id,
                          index: this.state.activeEvent.position
                        },
                        destination: {
                          listId: this.state.moveEventToList.id,
                          index: 0
                        }
                      });
                      this.closeMoveEventModal();
                    }}
                  >
                    Move
                  </Item>
                </div>
              </MobileModal.Header>
              <MobileModal.Content>
                {MoveEventModalContent}
              </MobileModal.Content>
            </MobileModal>}
          {this.state.window.width >= styleVariables.tabletBreakpoint &&
            <Modal
              open={this.state.addEventModalOpen}
              size='small'
              onClose={this.closeAddEventModal}
            >
              <Header content='Add Event' />
              <Modal.Content>
                {AddEventModalContent}
              </Modal.Content>
              <Modal.Actions style={{ textAlign: 'left' }}>
                <Button
                  basic
                  content='Cancel'
                  onClick={this.closeAddEventModal}
                />
              </Modal.Actions>
            </Modal>}
          {this.state.window.width < styleVariables.tabletBreakpoint &&
            <MobileModal open={this.state.addEventModalOpen}>
              <MobileModal.Header>
                <div className='menu'>
                  <Item as='a' onClick={this.closeAddEventModal}>
                    <Icon name='chevron left' />
                  </Item>
                </div>
              </MobileModal.Header>
              <MobileModal.Content>
                {AddEventModalContent}
              </MobileModal.Content>
            </MobileModal>}
          {this.state.window.width >= styleVariables.tabletBreakpoint &&
            <Modal
              open={this.state.addListModalOpen}
              size='small'
              onClose={this.closeAddListModal}
            >
              <Header content='Add New List' />
              <Modal.Content>
                {AddListModalContent}
              </Modal.Content>
              <Modal.Actions>
                <a
                  className={styles.modalCancel}
                  onClick={this.closeAddListModal}
                >
                  Cancel
                </a>
                <Button
                  primary
                  content='Create List'
                  onClick={() => this.refs.addListForm.submit()}
                />
              </Modal.Actions>
            </Modal>}
          {this.state.window.width < styleVariables.tabletBreakpoint &&
            <MobileModal open={this.state.addListModalOpen}>
              <MobileModal.Header>
                <div className='menu'>
                  <Item as='a' onClick={this.closeAddListModal}>
                    <Icon name='chevron left' />
                  </Item>
                </div>
              </MobileModal.Header>
              <MobileModal.Content>
                {AddListModalContent}
                <Button
                  primary
                  content='Create List'
                  onClick={() => this.refs.addListForm.submit()}
                />
              </MobileModal.Content>
            </MobileModal>}
          {viewer.pipelines.edges.length === 0 &&
            <div style={{ textAlign: 'center', padding: '2em' }}>
              <div>
                <h2>You have no pipelines</h2>
                <br />
                <Button
                  basic
                  icon='plus'
                  disabled={this.state.addingPipeline}
                  onClick={this.createPipeline}
                  labelPosition='left'
                  content='Add Pipeline'
                />
              </div>
            </div>}
          <DragDropContext onDragEnd={this.onDragEnd}>
            <Droppable
              droppableId='pipeline'
              type='COLUMN'
              direction='horizontal'
            >
              {boardProvided =>
                <div
                  className={styles.pipelineWrapper}
                  ref={boardProvided.innerRef}
                  {...boardProvided.droppableProps}
                >
                  {this.state.pipelines.edges.map(pipeline =>
                    <div key={pipeline.node.id} className={styles.pipeline}>
                      {pipeline.node.plists.edges.map((plist, index) =>
                        <Draggable
                          draggableId={plist.node.id}
                          key={plist.node.id}
                          index={index}
                        >
                          {provided =>
                            <div style={{ display: 'flex' }}>
                              <div
                                className={styles.list}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                              >
                                <div style={{ marginBottom: '1em' }} {...provided.dragHandleProps}>
                                  <div style={{ marginRight: '2em' }}>
                                    <ListTitle
                                      list={plist.node}
                                      relay={relay}
                                    />
                                  </div>
                                  <span className={styles.listMenu}>
                                    <Dropdown
                                      floating
                                      basic
                                      icon='ellipsis horizontal'
                                    >
                                      <Dropdown.Menu
                                        style={{ right: 0, left: 'auto' }}
                                      >
                                        <Dropdown.Item
                                          text='Add Event'
                                          onClick={this.openAddEventModal.bind(
                                            null,
                                            plist.node.id
                                          )}
                                        />
                                        <Dropdown.Divider />
                                        <Dropdown.Item
                                          text='Delete List'
                                          className={styles.negative}
                                          onClick={() => this.openListDeleteModal(
                                            {
                                              activePipelineId:
                                                pipeline.node.id,
                                              activeList: plist.node
                                            }
                                          )}
                                        />
                                      </Dropdown.Menu>
                                    </Dropdown>
                                  </span>
                                </div>
                                <div className={styles.listScrollWrapper}>
                                  <Droppable
                                    droppableId={plist.node.id}
                                    type='EVENT'
                                  >
                                    {(dropProvided) => (
                                      <div {...dropProvided.droppableProps} style={{ minHeight: '60vh' }} ref={dropProvided.innerRef}>
                                        <div>
                                          {plist.node.plist_events.edges.map((event, eventIndex) =>
                                            <Draggable key={event.node.id} draggableId={event.node.id} index={eventIndex}>
                                              {(dragProvided) => (
                                                <div>
                                                  <div
                                                    ref={dragProvided.innerRef}
                                                    {...dragProvided.draggableProps}
                                                    {...dragProvided.dragHandleProps}
                                                  >
                                                    <EventCard
                                                      basic
                                                      sortable
                                                      showDateLabel={false}
                                                      event={event.node.event.user_event}
                                                      timeZone={viewer.s_time_zone}
                                                      key={event.node.id}
                                                      relay={relay}
                                                      menu={eventSettingsMenu({
                                                        plist: plist.node,
                                                        plistEvent: event.node
                                                      })}
                                                    />
                                                  </div>
                                                  {dragProvided.placeholder}
                                                </div>
                                              )}
                                            </Draggable>
                                          )}
                                        </div>
                                        {dropProvided.placeholder}
                                      </div>
                                    )}
                                  </Droppable>
                                </div>
                                <div>
                                  <Button
                                    basic
                                    fluid
                                    onClick={this.openAddEventModal.bind(
                                      null,
                                      plist.node.id
                                    )}
                                    content='+ Add Event to List'
                                  />
                                </div>
                              </div>
                              {provided.placeholder}
                            </div>}
                        </Draggable>
                      )}
                      <div style={{ display: 'block' }}>
                        <div
                          className={styles.list}
                          onClick={this.openAddListModal.bind(
                            null,
                            pipeline.node.id
                          )}
                          style={{ cursor: 'pointer' }}
                        >
                          <h3>+ Add New List</h3>
                        </div>
                      </div>
                    </div>
                  )}
                </div>}
            </Droppable>
          </DragDropContext>
        </div>
      </div>
    );
  }
};

export default withAuth(Pipelines);



// WEBPACK FOOTER //
// ./webpack/components/Event/Pipelines/PipelinesComponent.js